import React, { useState, useEffect } from 'react';
import { Button } from '../shadComponents/ui/button';
import GalleryUserEditModal from './GalleryUserEditModal';
import GalleryUserCreateModal from './GalleryUserCreateModal';
import useAuthenticatedApi from '../Authentication/components/hooks/useAuthenticatedApi';
import SmallLoader from '../Common/components/SmallLoader';

interface GalleryUser {
    id: number;
    firstName: string;
    lastName: string;
    email: string | null;
    phoneNumber: string | null;
    igHandle: string | null;
    facialRecognitionOptedIn: boolean;
    needsRollupCommunication: boolean;
}

const GalleryUserList: React.FC = () => {
    const [users, setUsers] = useState<GalleryUser[]>([]);
    const [selectedUser, setSelectedUser] = useState<GalleryUser | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const authenticatedApi = useAuthenticatedApi();
    const [isPushingRollups, setIsPushingRollups] = useState(false);

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await authenticatedApi.get('/gallery-users');
            const data = response.data;
            setUsers(data);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const handleDelete = async (userId: number) => {
        if (!window.confirm('Are you sure you want to delete this user?'))
            return;

        try {
            await authenticatedApi.delete(`/gallery-users/${userId}`);
            fetchUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
        }
    };

    const pushRollupCommunication = async (userId: number) => {
        try {
            await authenticatedApi.post(`/gallery-users/${userId}/push-rollup`);
            fetchUsers();
        } catch (error) {
            console.error('Error pushing rollup communication:', error);
        }
    };

    const pushRollupCommunications = async () => {
        if (isPushingRollups) return;
        try {
            setIsPushingRollups(true);
            await authenticatedApi.post('/gallery-users/push-rollups');
            fetchUsers();
        } catch (error) {
            console.error('Error pushing rollup communications:', error);
        } finally {
            setIsPushingRollups(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold px-4">Gallery Users</h2>
                <div className="flex items-center justify-end gap-2">
                    <Button onClick={() => setIsCreateModalOpen(true)}>
                        Add User
                    </Button>
                    <Button onClick={() => pushRollupCommunications()}>
                        {isPushingRollups ? <SmallLoader /> : 'Push Rollups'}
                    </Button>
                </div>
            </div>

            <div className="bg-white rounded shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Contact
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Instagram
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Face Recognition
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {users.map((user) => (
                            <tr key={user.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {user.firstName} {user.lastName}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {user.email || user.phoneNumber}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {user.igHandle || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {user.facialRecognitionOptedIn
                                        ? 'Yes'
                                        : 'No'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap space-x-2">
                                    {user.needsRollupCommunication && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                pushRollupCommunication(
                                                    user.id,
                                                );
                                            }}
                                        >
                                            Push Rollup
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setSelectedUser(user);
                                            setIsEditModalOpen(true);
                                        }}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => handleDelete(user.id)}
                                    >
                                        Delete
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {selectedUser && (
                <GalleryUserEditModal
                    open={isEditModalOpen}
                    onOpenChange={setIsEditModalOpen}
                    user={selectedUser}
                    onUserUpdated={fetchUsers}
                />
            )}

            <GalleryUserCreateModal
                open={isCreateModalOpen}
                onOpenChange={setIsCreateModalOpen}
                onUserCreated={fetchUsers}
            />
        </div>
    );
};

export default GalleryUserList;
